import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import { RecurrenceEnum } from '@tyro/api';
import {
  RHFDatePicker,
  RHFRadioGroup,
  RHFSelect,
  RHFTextField,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import { RHFSelectGridPeriod } from '../../select-grid-period';
import { MINIMUM_EVENT_DURATION } from './constants';
import type { FormState } from './types';

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);

const recurrenceOptions: RecurrenceEnum[] = [
  RecurrenceEnum.NoRecurrence,
  RecurrenceEnum.Daily,
  RecurrenceEnum.Weekly,
  RecurrenceEnum.Biweekly,
  RecurrenceEnum.Monthly,
];

type ScheduleEventProps = {
  control: Control<FormState>;
  setValue: UseFormSetValue<FormState>;
  isEditing?: boolean;
};

export const ScheduleEvent = ({
  control,
  isEditing,
  setValue,
}: ScheduleEventProps) => {
  const { t } = useTranslation(['common', 'calendar']);

  const { allDayEvent, startDate, startTime, recurrenceEnum, ends } = useWatch({
    control,
  });

  const isRecurringEvent = recurrenceEnum !== RecurrenceEnum.NoRecurrence;

  return (
    <>
      <Stack direction="column" gap={1.5}>
        {/* <RHFSwitch
          label={t('common:allDay')}
          switchProps={{ color: 'primary' }}
          controlProps={{ name: 'allDayEvent', control }}
        /> */}

        <Grid container spacing={1}>
          <Grid size={{ xs: allDayEvent ? 12 : 6 }}>
            <RHFDatePicker
              label={t('calendar:inputLabels.startDate')}
              inputProps={{ fullWidth: true }}
              controlProps={{ name: 'startDate', control }}
            />
          </Grid>
          {!allDayEvent && (
            <>
              <Grid size={{ xs: 3 }}>
                <RHFSelectGridPeriod
                  label={t('common:startTime')}
                  controlProps={{ name: 'startTime', control }}
                  filterDay={startDate as dayjs.Dayjs}
                  onSelectPeriod={(period) => {
                    setValue('endTime', period.endTimeAsDate);
                  }}
                />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <RHFSelectGridPeriod
                  label={t('common:endTime')}
                  timePickerProps={{
                    minTime: dayjs(startTime as dayjs.Dayjs).add(
                      MINIMUM_EVENT_DURATION,
                      'minutes',
                    ),
                  }}
                  filterDay={startDate as dayjs.Dayjs}
                  periodType="end"
                  controlProps={{ name: 'endTime', control }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Stack>

      <RHFSelect
        label={t('calendar:inputLabels.schedule')}
        options={recurrenceOptions}
        getOptionLabel={(option) =>
          t(`calendar:inputLabels.recurrenceEnum.${option}`, {
            day: dayjs(startDate as dayjs.Dayjs).format('dddd'),
          })
        }
        controlProps={{
          name: 'recurrenceEnum',
          control,
        }}
      />

      {isEditing && isRecurringEvent ? (
        <RHFDatePicker
          label={t('calendar:inputLabels.endDate')}
          inputProps={{ fullWidth: true }}
          controlProps={{ name: 'endDate', control }}
        />
      ) : (
        recurrenceEnum &&
        isRecurringEvent && (
          <RHFRadioGroup
            label={t('calendar:inputLabels.ends')}
            radioGroupProps={{ sx: { gap: 1 } }}
            options={[
              {
                value: 'on',
                label: t('calendar:inputLabels.endsOn'),
              },
              {
                value: 'after',
                label: t('calendar:inputLabels.endsAfter'),
              },
            ]}
            renderOption={(option, renderRadio) => (
              <Stack key={option.value} direction="row">
                {renderRadio({
                  sx: {
                    width: '92px',
                  },
                })}
                <Box width="168px">
                  {option.value === 'on' && (
                    <RHFDatePicker
                      inputProps={{
                        variant: 'filled',
                        size: 'small',
                        hiddenLabel: true,
                        disabled: option.value !== ends,
                      }}
                      controlProps={{
                        name: 'endDate',
                        control,
                      }}
                    />
                  )}
                  {option.value === 'after' && (
                    <RHFTextField
                      textFieldProps={{
                        variant: 'filled',
                        size: 'small',
                        type: 'number',
                        hiddenLabel: true,
                        placeholder: '5',
                        disabled: option.value !== ends,
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="start">
                              {t('calendar:inputLabels.occurrences')}
                            </InputAdornment>
                          ),
                        },
                      }}
                      controlProps={{
                        name: 'occurrences',
                        control,
                      }}
                    />
                  )}
                </Box>
              </Stack>
            )}
            controlProps={{
              name: 'ends',
              control,
            }}
          />
        )
      )}
    </>
  );
};
